import { SocialMedia, SocialMediaPlatform } from "@shared/lib/types/influencer";
import { useEffect, useState } from "react";

import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useGetSocialMediaSearch } from "@entities/influencer/lib/hooks/useGetSocialMediaSearch";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { CircularProgress } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { Checkbox } from "@shared/ui/Checkbox";
import { InputNumber } from "@shared/ui/InputNumber";
import { Avatar, Divider, Input, Row, Select, Space } from "antd";
import classNames from "classnames";
import styles from "./styles.module.scss";

interface Props {
  handleAdd: (values: Partial<SocialMedia>) => void;
  isAdding: boolean;
  socialMediasToDisplay: string[];
}
export function SocialMediaForm({
  handleAdd,
  isAdding,
  socialMediasToDisplay,
}: Props) {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [BesearchQuery, setBeSearchQuery] = useState<string>("");

  const [selectedInfluencer, setSelectedInfluencer] = useState<
    Partial<SocialMedia> | undefined
  >(undefined);
  const [platform, setPlatform] = useState<string>(
    SocialMediaPlatform.Instagram
  );

  const { searchedInfluencers, isFetching } = useGetSocialMediaSearch(
    BesearchQuery,
    platform
  );

  const isSaveEnabled =
    Boolean(selectedInfluencer?.username) &&
    Boolean(selectedInfluencer?.link) &&
    ([SocialMediaPlatform.Instagram, SocialMediaPlatform.YouTube].includes(
      platform as SocialMediaPlatform
    )
      ? true
      : Boolean(platform));

  const [newPlatform, setNewPlatform] = useState<string>("");

  const [availablePlatforms, setAvailablPlatforms] = useState<string[]>(
    socialMediasToDisplay
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const addNewCategories = (roleName: string) => {
    if (!roleName.trim() || availablePlatforms.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availablePlatforms, roleName];
    setAvailablPlatforms(updatedRoles);
    setNewPlatform("");
    setDropdownOpen(false);
    setPlatform(roleName);
  };

  useEffect(() => {
    setSelectedInfluencer(undefined);
  }, [platform]);
  useEffect(() => {
    setAvailablPlatforms(socialMediasToDisplay);
  }, [socialMediasToDisplay]);

  return (
    <div className={styles.grid}>
      <div className={styles.itemGrid}>
        <div className={styles.title}>Platform</div>

        <Select
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
          showSearch
          placeholder="Select or create a Platform"
          optionFilterProp="children"
          allowClear
          value={platform}
          onChange={(value) => setPlatform(value)}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Space
                style={{
                  padding: "0 8px 4px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Input
                  placeholder="Enter new Platform"
                  value={newPlatform}
                  onChange={(e) => setNewPlatform(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newPlatform) {
                      e.stopPropagation();

                      e.preventDefault();
                      addNewCategories(newPlatform);
                    }
                  }}
                />
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  disabled={!newPlatform}
                  onClick={() => addNewCategories(newPlatform)}
                >
                  Add Platform
                </Button>
              </Space>
            </>
          )}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {availablePlatforms.map((role) => (
            <Select.Option key={role} value={role}>
              {role}
            </Select.Option>
          ))}
        </Select>
      </div>
      {[SocialMediaPlatform.Instagram, SocialMediaPlatform.YouTube].includes(
        platform as SocialMediaPlatform
      ) ? (
        <>
          <div className={styles.itemGrid}>
            <div className={styles.title}>Search</div>
            <Space>
              <Input
                value={searchQuery}
                placeholder={`Enter ${platform} username`}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button
                icon={<SearchOutlined />}
                onClick={() => setBeSearchQuery(searchQuery)}
                variant="outlined"
              />
            </Space>

            <div className={classNames(styles.grid, styles.content)}>
              {isFetching ? (
                <Row justify={"center"} style={{ width: "100%" }}>
                  <CircularProgress />
                </Row>
              ) : (
                <>
                  {(searchedInfluencers || []).length === 0 && (
                    <div className={styles.errorText}>
                      No {platform} user found
                    </div>
                  )}
                  {(searchedInfluencers || []).map((item) => (
                    <Checkbox
                      checked={item.username === selectedInfluencer?.username}
                      disabled={item.isLinkedToInfluencer}
                      onChange={(e) => {
                        if (e.target.checked) {
                          return setSelectedInfluencer(item);
                        }
                        setSelectedInfluencer(undefined);
                      }}
                    >
                      <Space>
                        <Avatar src={item.profilePicUrl} />

                        <Button
                          type="link"
                          href={item.link}
                          className={styles.itemGrid}
                        >
                          <div className={styles.name}>
                            <span style={{ marginRight: 4 }}>
                              {getPlatformIcon(platform)}
                            </span>
                            {item.username}
                          </div>
                          {item.isLinkedToInfluencer && (
                            <div className={styles.alreadyLinked}>
                              {item.username} is already linked by the creator
                              having name{" "}
                              <u>
                                {item?.linkedInfluencer?.firstName}{" "}
                                {item?.linkedInfluencer?.lastName}
                              </u>
                            </div>
                          )}
                        </Button>
                      </Space>
                    </Checkbox>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.itemGrid}>
            <div className={styles.title}>Username</div>
            <Input
              value={selectedInfluencer?.username}
              placeholder="Enter username"
              onChange={(e) =>
                setSelectedInfluencer((prev) => ({
                  ...prev,
                  username: e.target.value || "",
                }))
              }
            />
          </div>
          <div className={styles.itemGrid}>
            <div className={styles.title}>Link</div>
            <Input
              value={selectedInfluencer?.link}
              placeholder="Enter link"
              onChange={(e) =>
                setSelectedInfluencer((prev) => ({
                  ...prev,
                  link: e.target.value || "",
                }))
              }
            />
          </div>
          <div className={styles.itemGrid}>
            <div className={styles.title}>Followers</div>
            <InputNumber
              value={selectedInfluencer?.followers}
              placeholder="Enter followers"
              type="number"
              onChange={(value) =>
                setSelectedInfluencer((prev) => ({
                  ...prev,
                  followers: Number(value) || undefined,
                }))
              }
            />
          </div>
        </>
      )}
      <div className={styles.footer}>
        <Button
          type="primary"
          disabled={!isSaveEnabled}
          loading={isAdding}
          onClick={() =>
            selectedInfluencer &&
            handleAdd({ ...selectedInfluencer, platform: platform })
          }
        >
          {isAdding ? "Adding" : "Add social Media"}
        </Button>
      </div>
    </div>
  );
}
